export enum ELhAppRoles {
  ExagoAllAccess = 'ExagoAllAccess',
}

export enum EExagoQueryKeys {
  SiteIds = 'SiteIds',
  AccountIds = 'AccountIds',
  StartDate = 'StartDate',
  EndDate = 'EndDate',
  ReportStartDate = 'ReportStartDate',
  ReportEndDate = 'ReportEndDate',
  ReportAccount = 'ReportAccount',
  ReportSite = 'ReportSite',
  Month1 = 'Month1',
  Month2 = 'Month2',
  M1Formatted = 'M1Formatted',
  M2Formatted = 'M2Formatted',
  Scorecard = 'Scorecard',
}

export interface IExagoQuery {
  userId: number;
  siteIds: string;
  accountIds: string;
  startDate: string;
  endDate: string;
  reportStartDate: string;
  reportEndDate: string;
  siteNames: string;
  accounts: string;
  month1: string;
  month2: string;
  m1Formatted: string;
  m2Formatted: string;
  scorecardIds: string;
}

// https://exagobi.com/support/administrators/rest-web-service-api/sessions/
export interface IExagoSessionRequest {
  Page?: string;
  ApiAction?: EWrApiAction;
  ExportType?: EWrExportType;
  ShowTabs?: boolean;
  ReportSettings?: IExagoReportRequest;
  ShowErrorDetail?: boolean;
}

export interface IExagoSession {
  Page: string;
  ApiAction: EWrApiAction;
  ExportType: EWrExportType;
  ShowTabs: boolean;
  ReportSettings: IExagoReport;
  ShowErrorDetail: boolean;
  readonly Id: string;
  readonly AppUrl: string;
  readonly ApiKey: string;
}

export interface IExagoReport {
  Id: string;
  ReportPath: string;
  SortsResource: ISorts;
  FilterItems: IFilter[];
  readonly IsError: boolean;
  readonly ErrorList: EReportValidationErrorType[];
}

export interface IExagoReportRequest {
  ReportPath: string;
  SortsResource?: ISorts;
  FilterItems?: IFilterRequest[];
}

export interface IExagoFolderNode {
  name: string;
  leaf_flag: boolean;
  readonly_flag: boolean;
  type: EWrReportType | null | string;
  user: ELhAppRoles;
  entity: IExagoFolderNode[] | null;
  content_id: string;
  content_type: EWrContentType;
  report_type: number;
  access_flags: number;
}
// 👆👇 https://exagobi.com/support/administrators/rest-web-service-api/folders
export class ExagoFolderNodeModel implements IExagoFolderNode {
  name: string;
  leaf_flag: boolean;
  readonly_flag: boolean;
  type: EWrReportType | null | string;
  user: ELhAppRoles;
  entity: IExagoFolderNode[] | null;
  content_id: string;
  content_type: EWrContentType;
  report_type: number;
  access_flags: number;

  constructor(data: IExagoFolderNode) {
    this.name = data.name ?? '';
    this.leaf_flag = data.leaf_flag ?? false;
    this.readonly_flag = data.readonly_flag ?? false;
    this.type = data.type ?? null;
    this.user = data.user ?? '';
    this.entity = data.entity ?? [];
    this.content_id = data.content_id ?? '';
    this.content_type = data.content_type ?? EWrContentType.Folder;
    this.report_type = data.report_type ?? null;
    // eslint-disable-next-line no-magic-numbers
    this.access_flags = data.access_flags ?? -1;
  }
}

export interface IExagoFolders {
  entity: IExagoFolderNode[];
}

export interface ISorts {
  ReplaceFlag: string;
  SortItems: ISortItem;
}

export interface ISortItem {
  Id: string;
  Asc: boolean;
}

export interface IFilter {
  Name: string;
  EntityName: string;
  ColumnName: string;
  DataType: EDataType;
  Operator: EFilterOperator;
  Values: string[];
  AndFlag: boolean;
  Prompt: boolean;
  GroupWithNext: boolean;
}

export interface IFilterRequest {
  Name: string;
  DataType: EDataType;
  Values: string[];
  Operator?: EFilterOperator;
}

// https://exagobi.com/support/administrators/rest-web-service-api/batch/
export interface IExagoBatchRequest<T = unknown> {
  Url: string;
  Method: EExagoBatchMethodType;
  Payload?: T;
}

export interface IExagoBatchResponse {
  ReturnCode: string;
  ResponseContent: unknown;
}

// https://exagobi.com/support/administrators/net-api/constants-and-enumerators/

export enum EWrApiAction {
  Default,
  Home,
  ExecuteReport,
  EditReport,
  NewAdvancedReport,
  NewCrossTabReport,
  NewExpressReport,
  NewDashboardReport,
  NewExpressView,
  ScheduleReport,
  ScheduleReportsManager,
}

export enum EWrContentType {
  Report,
  Folder,
  Theme,
  Template,
  // eslint-disable-next-line no-magic-numbers
  All = 99,
}

export enum EWrReportType {
  Advanced = 'advanced',
  Express = 'express',
  Dashboard = 'dashboard',
  Chained = 'chained',
  ExpressView = 'expressview',
}

export enum EWrExportType {
  Html,
  Excel,
  Pdf,
  Rtf,
  Csv,
  Word,
  Default,
}

export enum EDataType {
  String,
  Date,
  DateTime,
  Time,
  Integer,
  Decimal,
  Float,
  Bit,
  Guid,
  Image,
  Currency,
}

export enum EFilterOperator {
  EqualTo,
  NotEqualTo,
  LessThan,
  LessThanOrEqualTo,
  GreaterThan,
  GreaterThanOrEqualTo,
  StartsWith,
  NotStartsWith,
  EndsWith,
  NotEndsWith,
  Contains,
  NotContains,
  Between,
  NotBetween,
  OneOf,
  NotOneOf,
}

export enum EReportValidationErrorType {
  SortFormulaSyntaxError,
  FilterFormulaSyntaxError,
  DataObjectNotFound,
  SortDataFieldNotFound,
  FilterDataFieldNotFound,
  LinkedDataFieldNotFound,
  MinMaxFilterDataFieldNotFound,
  JoinDataObjectNotFound,
  JoinNotFound,
  JoinDataFieldNotFound,
  ChartDataFieldNotFound,
  MapDataFieldNotFound,
  CellDataFieldNotFound,
  RowGroupNameNotFound,
  RowGroupFormulaNameNotFound,
  ChartCellIdNotFound,
  MapCellIdNotFound,
  MergedCellsAcrossSections,
  CrossTabIdNotFound,
  CrossTabCellIdNotFound,
  ColumnSortByFieldNotFound,
  ChildReportNotFound,
  ExpressViewColumnMissingEntity,
  ExpressViewColumnMissingField,
  ExpressViewGroupMissingField,
  ExpressViewFilterMissingField,
  ExpressViewChartMissingField,
  EtlReportDesignerModeNotEtl,
  EtlReportTypeNotAdvanced,
  EtlDataTypeNotSet,
  EtlDataTypeNotFound,
  EtlReportFormatInvalid,
  EtlCellBlank,
  EtlHeaderCellValueDuplicate,
  EtlPossibleDataTypeError,
  EtlCellSpan,
  None,
  ReportParameterNameUsed,
}

// https://exagobi.com/support/administrators/rest-web-service-api/batch/
export enum EExagoBatchUrlType {
  DataSources = 'DataSources',
  Entities = 'Entities',
  Fields = 'Fields',
  Folders = 'Folders',
  Functions = 'Functions',
  Joins = 'Joins',
  Parameters = 'Parameters',
  Reports = 'Reports',
  Roles = 'Roles',
  SchedulesV2 = 'SchedulesV2',
  ServerEvents = 'ServerEvents',
  Sessions = 'Sessions',
  Settings = 'Settings',
  StorageMgmt = 'StorageMgmt',
  List = 'List',
}

export enum EExagoBatchMethodType {
  POST = 'POST',
  PATCH = 'PATCH',
  GET = 'GET',
  DELETE = 'DELETE',
}

// https://exagobi.com/support/administrators/rest-web-service-api/introduction-2/
// eslint-disable-next-line no-magic-numbers
export const SUCCESS_STATUS_CODES: number[] = [200, 201, 204];
// eslint-disable-next-line no-magic-numbers
export const ERROR_STATUS_CODES: number[] = [400, 401, 404, 409, 500];

export const REPORTING_REPORT_TYPES: string[] = [EWrReportType.ExpressView, EWrReportType.Advanced];
